import { useCallback, useEffect, useState } from "react";

import type { Invoice } from "~/shared/schemas/invoice-schema";

type PaymentAmounts = Record<string, number>;

export const usePaymentAmounts = (invoices: Invoice[]) => {
	const [amounts, setAmounts] = useState<PaymentAmounts>({});

	// Initialize amounts when invoices change, while preserving existing edits
	useEffect(() => {
		setAmounts((prev) => {
			const nextAmounts: PaymentAmounts = {};
			for (const invoice of invoices) {
				const id = String(invoice.invoiceID);
				// Keep existing edited amount or initialize to balanceAmt
				nextAmounts[id] =
					id in prev ? (prev[id] ?? 0) : (invoice.balanceAmt ?? 0);
			}
			return nextAmounts;
		});
	}, [invoices]);

	// Update a specific amount
	const updateAmount = useCallback((invoiceId: string, amount: number) => {
		setAmounts((prev) => ({
			...prev,
			[invoiceId]: amount,
		}));
	}, []);

	// Calculate total payment
	const totalPayment = Object.values(amounts).reduce(
		(acc, curr) => acc + curr,
		0,
	);

	// Reset all amounts
	const resetAmounts = useCallback(() => {
		const resetAmounts: PaymentAmounts = {};
		for (const invoice of invoices) {
			resetAmounts[String(invoice.invoiceID)] = invoice.balanceAmt ?? 0;
		}
		setAmounts(resetAmounts);
	}, [invoices]);

	return {
		amounts,
		updateAmount,
		totalPayment,
		resetAmounts,
	};
};
